// One story arc.
//
// Every card on /arcs linked to /arcs/<slug>, and no such route existed: all
// 33 links quietly served the homepage, which is a dead end for a reader and a
// duplicate of the homepage for a crawler. This is the page they were pointing
// at — what happens in the arc, where it sits in the story, what to read or
// watch, and the arcs either side of it.

import React from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import storyArcs from '../data/storyArcs';
import animeConfig from '../config/animeConfig';
import './Guides.css';
import './ArcsPage.css';

// Flat list once, so previous/next reads across saga boundaries the way the
// story actually runs.
const FLAT = storyArcs.flatMap((saga) =>
  saga.arcs.map((arc) => ({ ...arc, saga: saga.saga, sagaType: saga.sagaType })),
);

const ArcDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const index = FLAT.findIndex((a) => a.slug === slug);
  const arc = index >= 0 ? FLAT[index] : null;
  const previous = index > 0 ? FLAT[index - 1] : null;
  const next = index >= 0 && index < FLAT.length - 1 ? FLAT[index + 1] : null;

  const bgStyle = {
    backgroundImage: `linear-gradient(to bottom, rgba(0, 0, 0, 0.55), rgba(0, 0, 0, 0.85)), url(${animeConfig.onePiece.backgroundImage})`,
  };

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!arc) {
    return (
      <div className="guide-page-wrapper" style={bgStyle}>
        <Helmet>
          <title>Arc Not Found | One Piece Trivia</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <div className="guide-container" style={{ textAlign: 'center', paddingTop: '80px' }}>
          <h1>Arc Not Found</h1>
          <p>That arc is not on the list.</p>
          <Link to="/arcs" className="guide-cta-button">See every arc</Link>
        </div>
      </div>
    );
  }

  const position = `Arc ${index + 1} of ${FLAT.length}`;
  const description = `${arc.desc} ${arc.name} is ${position.toLowerCase()} in One Piece, part of the ${arc.saga}: chapters ${arc.chapters}, episodes ${arc.episodes}.`;

  return (
    <>
      <Helmet>
        <title>{`${arc.name} Arc (One Piece): Chapters ${arc.chapters}, Episodes ${arc.episodes}`}</title>
        <meta name="description" content={description.slice(0, 155)} />
        <link rel="canonical" href={`https://www.onepiecetrivia.com/arcs/${arc.slug}`} />
        <meta property="og:title" content={`${arc.name} Arc — One Piece`} />
        <meta property="og:description" content={arc.desc} />
        <meta property="og:url" content={`https://www.onepiecetrivia.com/arcs/${arc.slug}`} />
        {arc.image && <meta property="og:image" content={`https://www.onepiecetrivia.com${arc.image}`} />}
      </Helmet>

      <div className="guide-page-wrapper" style={bgStyle}>
        <div className="guide-container">

          <p className="arc-breadcrumb">
            <Link to="/arcs">Story Arcs</Link> / {arc.saga} / {arc.name}
          </p>

          <h1>{arc.name} Arc</h1>
          {arc.filler && <span className="arc-filler-badge">Filler arc</span>}

          <p className="guide-intro">{arc.desc}</p>

          {arc.image && (
            <img src={arc.image} alt={`${arc.name} arc`} className="arc-detail-image" />
          )}

          <div className="arc-facts">
            <div className="arc-fact">
              <span className="arc-fact-label">Saga</span>
              <span className="arc-fact-value">{arc.saga}</span>
            </div>
            <div className="arc-fact">
              <span className="arc-fact-label">Chapters</span>
              <span className="arc-fact-value">{arc.chapters}</span>
            </div>
            <div className="arc-fact">
              <span className="arc-fact-label">Episodes</span>
              <span className="arc-fact-value">{arc.episodes}</span>
            </div>
            <div className="arc-fact">
              <span className="arc-fact-label">Position</span>
              <span className="arc-fact-value">{position}</span>
            </div>
          </div>

          <div className="arc-neighbours">
            {previous ? (
              <Link to={`/arcs/${previous.slug}`} className="arc-neighbour">
                <span className="arc-neighbour-label">Before this</span>
                <span className="arc-neighbour-name">{previous.name}</span>
              </Link>
            ) : <span />}
            {next && (
              <Link to={`/arcs/${next.slug}`} className="arc-neighbour arc-neighbour-next">
                <span className="arc-neighbour-label">After this</span>
                <span className="arc-neighbour-name">{next.name}</span>
              </Link>
            )}
          </div>

          <div className="guide-cta">
            <h2>Know this arc?</h2>
            <p>Put it to the test — the characters from it turn up in every game.</p>
            <div className="guide-games-row">
              <button className="guide-game-btn" onClick={() => navigate('/quiz')}>
                <img src="/character-quiz-and-any-related.png" alt="" className="guide-game-icon" />
                <span>Character Quiz</span>
              </button>
              <button className="guide-game-btn" onClick={() => navigate('/image-quiz')}>
                <img src="/image-quiz-and-any-related.png" alt="" className="guide-game-icon" />
                <span>Image Quiz</span>
              </button>
              <button className="guide-game-btn" onClick={() => navigate('/devil-fruit-quiz')}>
                <img src="/devil-fruit-quiz-and-any-related.png" alt="" className="guide-game-icon" />
                <span>Devil Fruit Quiz</span>
              </button>
              <button className="guide-game-btn" onClick={() => navigate('/bounty-battle')}>
                <span className="guide-game-icon game-emoji" aria-hidden="true">💰</span>
                <span>Bounty Battle</span>
              </button>
              <button className="guide-game-btn" onClick={() => navigate('/puzzle')}>
                <span className="guide-game-icon game-emoji" aria-hidden="true">🧩</span>
                <span>Jigsaw Puzzle</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ArcDetail;
